import { memo, useCallback, useEffect, useRef, useState } from "react";

import { getRoleUsersJustAccess, updateRoleUsers } from "@/api/role-user";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient
} from "@tanstack/react-query";
import { HashLoader } from "react-spinners";
import { useDebouncedCallback } from "use-debounce";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface Props {
  roleId: number;
  isOpen: boolean;
  onClose: () => void;
  onSaveSelectedUsers?: (userIds: number[]) => void;
  initialSelectedUserIds?: number[];
}

const PAGE_SIZE = 10;

const RoleUserListModal: React.FC<Props> = ({
  isOpen,
  onClose,
  roleId,
  onSaveSelectedUsers,
  initialSelectedUserIds
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>انتخاب کاربر ها</DialogTitle>
        </DialogHeader>

        <Content
          roleId={roleId}
          onClose={onClose}
          onSaveSelectedUsers={onSaveSelectedUsers}
          initialSelectedUserIds={initialSelectedUserIds}
        />
      </DialogContent>
    </Dialog>
  );
};

const Content = memo(
  ({
    roleId,
    onClose,
    onSaveSelectedUsers,
    initialSelectedUserIds
  }: {
    roleId: number;
    onClose: () => void;
    onSaveSelectedUsers?: (userIds: number[]) => void;
    initialSelectedUserIds?: number[];
  }) => {
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

    const observerRef = useRef<IntersectionObserver | null>(null);

    const queryClient = useQueryClient();

    // 🔥 Infinite Query
    const { data, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } =
      useInfiniteQuery({
        queryKey: ["role-user-just-access", roleId, debouncedSearch],

        queryFn: ({ pageParam = 1 }) =>
          getRoleUsersJustAccess(roleId, {
            page: pageParam,
            size: PAGE_SIZE,
            search: debouncedSearch
          }),

        initialPageParam: 1,

        getNextPageParam: (lastPage, pages) => {
          const totalCount = lastPage?.totalCount ?? 0;

          const loadedItems = pages.flatMap((page) => page?.data ?? []).length;

          return loadedItems < totalCount ? pages.length + 1 : undefined;
        }
      });

    // 🔥 flatten pages
    const users = data?.pages.flatMap((page) => page?.data || []) || [];

    // 🔥 init selected users
    useEffect(() => {
      if (initialSelectedUserIds && initialSelectedUserIds.length > 0) {
        setSelectedUsers(initialSelectedUserIds);
        return;
      }

      setSelectedUsers((prev) => {
        const backendSelected = users
          .filter((x) => x.isAccess)
          .map((x) => x.id);

        return [...new Set([...prev, ...backendSelected])];
      });
    }, [data, initialSelectedUserIds]);

    // 🔥 mutation
    const { mutate, isPending } = useMutation({
      mutationFn: () => updateRoleUsers(roleId, selectedUsers),

      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["role-user-just-access", roleId]
        });

        onClose();
      }
    });

    // 🔥 checkbox toggle
    const changeHandler = (userId: number) => {
      setSelectedUsers((prev) =>
        prev.includes(userId)
          ? prev.filter((id) => id !== userId)
          : [...prev, userId]
      );
    };

    // 🔥 debounce search
    const handleSearch = useDebouncedCallback((value: string) => {
      setDebouncedSearch(value);
    }, 700);

    // 🔥 infinite scroll observer
    const lastItemRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (isFetchingNextPage) return;

        if (observerRef.current) {
          observerRef.current.disconnect();
        }

        observerRef.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            fetchNextPage();
          }
        });

        if (node) {
          observerRef.current.observe(node);
        }
      },
      [isFetchingNextPage, hasNextPage, fetchNextPage]
    );

    return (
      <>
        {/* 🔍 SEARCH */}
        <Input
          placeholder="جستجو"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            handleSearch(e.target.value);
          }}
          className="w-full md:w-1/2"
        />

        {/* 📜 LIST */}
        <div className="mt-5 h-64 divide-y divide-slate-300 overflow-y-auto rounded-md border border-slate-300">
          {isFetching && !users.length ? (
            <div className="flex h-full items-center justify-center">
              <HashLoader color="#0099A5" size={40} />
            </div>
          ) : users.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <p className="text-sm text-slate-500">نتیجه‌ای یافت نشد</p>
            </div>
          ) : (
            <>
              {users
                .filter((user) => (onSaveSelectedUsers ? user.isAccess : true))
                .map((user, index) => {
                  const isLast = index === users.length - 1;

                  return (
                    <div
                      key={user.id}
                      ref={isLast ? lastItemRef : null}
                      className="flex items-center gap-x-2 px-2 py-4"
                    >
                      <Checkbox
                        checked={selectedUsers.includes(user.id)}
                        onCheckedChange={() => changeHandler(user.id)}
                      />

                      <div className="flex flex-col">
                        <p className="line-clamp-1 text-sm">{user.name}</p>

                        <span className="text-xs text-slate-500">
                          {user.userName}
                        </span>
                      </div>
                    </div>
                  );
                })}

              {/* 🔥 loading next page */}
              {isFetchingNextPage && (
                <div className="flex justify-center py-4">
                  <HashLoader color="#0099A5" size={25} />
                </div>
              )}
            </>
          )}
        </div>

        {/* 💾 SAVE */}
        <Button
          className="mt-5 w-full"
          disabled={isPending}
          onClick={() => {
            if (onSaveSelectedUsers) {
              onSaveSelectedUsers(selectedUsers);
              onClose();
              return;
            }

            mutate();
          }}
        >
          {isPending ? "در حال ذخیره..." : "ذخیره"}
        </Button>
      </>
    );
  }
);

export default RoleUserListModal;
